import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

// this is optimistic pending segments store for atlas doris.
export interface OptimisticPendingSegmentsState {
  // optimistic pending segments STATE
  optimisticPendingSegments: Set<string>;
  // API-derived processing segments (from GetProcessingSegments), shared globally so
  // the segments list and the global poller read the same source of truth.
  processingSegments: string[];
  // Number of mounted consumers (e.g. the segments list page) that require polling to
  // run continuously while they are visible, even when nothing is optimistically pending.
  pollSubscriberCount: number;

  // optimistic pending segments ACTIONS
  addOptimisticPendingSegment: (segmentId: string) => void;
  removeOptimisticPendingSegment: (segmentId: string) => void;
  clearOptimisticPendingSegments: () => void;
  setProcessingSegments: (segmentIds: string[]) => void;
  incrementPollSubscribers: () => void;
  decrementPollSubscribers: () => void;

  // Selectors
  isOptimisticallyPending: (segmentId: string) => boolean;
  getOptimisticPendingSegmentIds: () => string[];
}

/**
 * Zustand store for managing segments that should appear as pending
 * immediately after creation, before the API confirms their processing status
 */
export const useOptimisticPendingSegmentsStore = create<OptimisticPendingSegmentsState>()(
  devtools(
    (set, get) => ({
      // Initial state
      optimisticPendingSegments: new Set<string>(),
      processingSegments: [],
      pollSubscriberCount: 0,

      // Actions
      addOptimisticPendingSegment: (segmentId: string) => {
        set((state) => ({
          optimisticPendingSegments: new Set(state.optimisticPendingSegments).add(segmentId)
        }));
      },

      removeOptimisticPendingSegment: (segmentId: string) => {
        set((state) => {
          const newSet = new Set(state.optimisticPendingSegments);
          newSet.delete(segmentId);
          return { optimisticPendingSegments: newSet };
        });
      },

      clearOptimisticPendingSegments: () => {
        set({ optimisticPendingSegments: new Set<string>() });
      },

      setProcessingSegments: (segmentIds: string[]) => {
        set({ processingSegments: segmentIds });
      },

      incrementPollSubscribers: () => {
        set((state) => ({ pollSubscriberCount: state.pollSubscriberCount + 1 }));
      },

      decrementPollSubscribers: () => {
        set((state) => ({ pollSubscriberCount: Math.max(0, state.pollSubscriberCount - 1) }));
      },

      // Selectors
      isOptimisticallyPending: (segmentId: string) => {
        return get().optimisticPendingSegments.has(segmentId);
      },

      getOptimisticPendingSegmentIds: () => {
        return Array.from(get().optimisticPendingSegments);
      }
    }),
    { name: 'optimistic-pending-segments-store' }
  )
);
